import {
  Completion,
  CompletionContext,
  CompletionResult,
  startCompletion,
} from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import { type ReactElement } from 'react';

import type { IntlProps } from '../utils/render-node.js';
import { render } from '../utils/render-node.js';

/**
 * @internal
 */
export type AutocompleteProvider = (
  context: CompletionContext,
) => Promise<AutocompletionResult | null>;

/**
 * @internal
 */
export interface AutocompletionResult
  extends Omit<CompletionResult, 'options'> {
  suggestions: Suggestion[];
  /** @defaultValue false */
  reopen?: boolean | ((suggestions: Completion) => Promise<boolean>);
  keepOrder?: boolean;
}

/**
 * @internal
 */
export interface Suggestion
  extends Pick<Completion, 'label' | 'apply' | 'boost' | 'section'> {
  prefix?:
    | (({ suggestion }: { suggestion: Suggestion }) => ReactElement)
    | string;
  postfix?:
    | (({ suggestion }: { suggestion: Suggestion }) => ReactElement)
    | string;
  popup?: ({ suggestion }: { suggestion: Suggestion }) => ReactElement;
  type?: string;
}

/** Apply completion and reopen completions */
async function applyAndReopen(
  view: EditorView,
  from: number,
  to: number,
  completion: Completion,
  apply: Completion['apply'],
  reopen: boolean | ((suggestion: Completion) => Promise<boolean>),
) {
  if (apply === undefined || typeof apply === 'string') {
    const value = apply || completion.label;
    view.dispatch({
      changes: { from, to, insert: value },
      selection: { anchor: from + value.length },
      userEvent: 'input.complete',
    });
  } else {
    apply(view, completion, from, to);
  }
  if (typeof reopen === 'function' ? await reopen(completion) : reopen) {
    startCompletion(view);
  }
}

export const autocompletionExtension = (
  autocompleteProvider: AutocompleteProvider,
  intlProps: IntlProps,
): ((ctx: CompletionContext) => Promise<CompletionResult | null>) => {
  return async (ctx: CompletionContext): Promise<CompletionResult | null> => {
    const result = await autocompleteProvider(ctx);
    if (!result) {
      return null;
    }
    return {
      ...result,
      options: result.suggestions.map((s, i) => ({
        ...s,
        info: s.popup
          ? (completion) => render(completion, s.popup, intlProps) as Node
          : undefined,
        apply: (view, completion, from, to) =>
          applyAndReopen(
            view,
            from,
            to,
            completion,
            s.apply,
            result.reopen ?? false,
          ),
        ...(result.keepOrder && { boost: 99 - i }),
      })),
    };
  };
};
